import { Layer } from "../models/maps/layer";
import { LayerPublic } from "../models/maps/layerPublic";
import { InfraPlan } from "../models/plan/plan";

import { BaseService } from "./base.service";
import { plannenService } from "./plannen.service";
import { eventBus } from "./event-emitter.service";
import { helperService } from "./helper.service";
import { mapService } from "./map.service";

import { FeatureCollection } from 'geojson';
import * as atlas from 'azure-maps-control';


export class LayerService extends BaseService {
  private static instance: LayerService;

  // Method to return the singleton instance
  public static getInstance(): LayerService {
    if (!LayerService.instance) {
      LayerService.instance = new LayerService();
    }
    return LayerService.instance;
  }

  public parseWmsBaseUrl = (url: string) => {
    const [base, query] = url.split("?");
    const params = new URLSearchParams(query);
  
    return {
      base,
      params
    };
  };

  public checkGetFeatureInfoSupport = async (
    baseUrl: string,
    layerName: string
  ) => {
    const url = `${baseUrl}?service=WMS&request=GetCapabilities`;
  
    const res = await fetch(url);
    const text = await res.text();
  
    // Check of GetFeatureInfo bestaat
    const hasFeatureInfo = text.includes("GetFeatureInfo");
  
    if (!hasFeatureInfo) {
      // console.log("Geen GetFeatureInfo support");
      return false;
    }
  
    // Queryable check
    const queryable = this.isLayerQueryable(text, layerName);
    if (!queryable) {
      // console.log(`Layer "${layerName}" is niet queryable`);
      return false;
    }
  
    return true;
  };

  public isLayerQueryable = (
    capabilitiesXml: string,
    layerName: string
  ): boolean => {
    const parser = new DOMParser();
    const xml = parser.parseFromString(capabilitiesXml, "text/xml");
  
    const layers = Array.from(xml.getElementsByTagName("Layer"));
  
    for (const layer of layers) {
      const nameEl = layer.getElementsByTagName("Name")[0];
  
      if (!nameEl) continue;
  
      const name = nameEl.textContent;
  
      if (name === layerName) {
        const queryableAttr = layer.getAttribute("queryable");
  
        return queryableAttr === "1";
      }
    }
  
    return false;
  };

  public buildGetFeatureInfoUrl = ({
    base,
    params,
    map,
    pixel,
    infoFormat = "application/json"
  }: {
    base: string;
    params: URLSearchParams;
    map: any;
    pixel: [number, number];
    infoFormat?: string;
  }) => {
    const camera = map.getCamera();
    const bounds = camera.bounds; // [west, south, east, north] in WGS84 (EPSG:4326)

    // Use the CSS pixel size of the map canvas so it matches the click pixel (e.pixel),
    // which is also in CSS pixels — the device-pixel-ratio-scaled canvas.width/height would not.
    const canvas = map.getCanvas();
    const width = canvas.clientWidth || canvas.width;
    const height = canvas.clientHeight || canvas.height;

    const layers = params.get("layers");

    // WMS 1.3.0 with EPSG:4326 uses lat,lon axis order → south,west,north,east.
    const bbox = [bounds[1], bounds[0], bounds[3], bounds[2]].join(",");

    const query = new URLSearchParams({
      SERVICE: "WMS",
      VERSION: "1.3.0",
      REQUEST: "GetFeatureInfo",
      LAYERS: layers || "",
      QUERY_LAYERS: layers || "",
      STYLES: "",
      CRS: "EPSG:4326",
      BBOX: bbox,
      WIDTH: width.toString(),
      HEIGHT: height.toString(),
      INFO_FORMAT: infoFormat,
      FEATURE_COUNT: "10",
      I: Math.round(pixel[0]).toString(),
      J: Math.round(pixel[1]).toString()
    });
  
    return `${base}?${query.toString()}`;
  };

  // Helpers

  public buildWmsUrl = (layer: LayerPublic): string => {
    const params = [];
    params.push("bbox={bbox-epsg-3857}");
  
    return `${layer.url}?${params.join("&")}`;
  };

  public buildWfsUrl = (
    baseUrl: string, 
    layerName: string, 
    bbox: number[]
  ) => {
    const [west, south, east, north] = bbox;
  
    const params = new URLSearchParams({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      typeNames: layerName,
      outputFormat: "application/json",
      srsName: "EPSG:4326",
      bbox: `${south},${west},${north},${east},EPSG:4326`
    });
  
    return `${baseUrl}?${params.toString()}`;
  };

  // ─── GetFeatureInfo (public WMS layers) ────────────────────────────────────

  /**
   * Query the given active WMS layers with GetFeatureInfo at the clicked pixel
   * and render the returned feature properties in the map popup.
   * Returns true when at least one layer returned properties.
   */
  public async showFeatureInfoPopup(
    e: any,
    map: any,
    popupRef: React.MutableRefObject<atlas.Popup | null>,
    layers: LayerPublic[] | Layer[]
  ): Promise<boolean> {
    const position: atlas.data.Position | undefined = e?.position;
    if (!position || !this.isValidPosition(position)) return false;

    const results = await this.getFeatureInfoForLayers(e, map, layers);
    if (results.length === 0) return false;

    if (!popupRef.current) {
      popupRef.current = new atlas.Popup({ pixelOffset: [0, -20], closeButton: true });
    }

    const content = this.buildFeatureInfoPopupContent(results);

    // Keep popup fully visible in the viewport and anchor on the exact click position.
    // Size constraints are handled in CSS on popup container/content.

    try {
      popupRef.current.setOptions({
        content,
        position,
        pixelOffset: [0, 0],
        closeButton: true,
        closeOnDrag: true,
        showPointer: true,
      });
      popupRef.current.open(map);

      // Bind CSV download button
      setTimeout(() => {
        const csvBtn = document.getElementById('link-download-feature-csv');
        if (csvBtn) {
          csvBtn.addEventListener('click', (ev) => {
            ev.preventDefault();
            this.downloadFeatureInfoCsv(results);
          });
        }
      }, 100);
    }
    catch (error) {
      console.error("Error opening feature info popup:", error, { position });
      return false;
    }

    return true;
  }

  /**
   * Fetches GetFeatureInfo for each layer and returns the parsed feature
   * properties grouped per layer (only layers that returned data are included).
   */
  public async getFeatureInfoForLayers(
    e: any,
    map: any,
    layers: LayerPublic[] | Layer[]
  ): Promise<Array<{ layerDisplayName: string; features: Array<Record<string, any>> }>> {
    const results: Array<{ layerDisplayName: string; features: Array<Record<string, any>> }> = [];

    for (const layer of layers) {
      try {
        const features = await this.fetchWmsFeatureInfo(e, map, layer);
        if (features.length > 0) {
          results.push({
            layerDisplayName: layer.layerDisplayName || layer.sourceName || "Kaartlaag",
            features
          });
        }
      }
      catch (err) {
        console.error("GetFeatureInfo error:", err, { layer: layer.id });
      }
    }

    return results;
  }

  /**
   * Performs the GetFeatureInfo request for a single WMS layer.
   * Tries INFO_FORMAT=application/json first and falls back to text/html
   * for servers that do not support JSON (e.g. ArcGIS).
   */
  private async fetchWmsFeatureInfo(
    e: any,
    map: any,
    layer: LayerPublic | Layer
  ): Promise<Array<Record<string, any>>> {
    const { base, params } = this.parseWmsBaseUrl(layer.url);
    // e.pixel is the click location in CSS pixels (I/J); e.position is geographic and is not used here.
    const pixel = e.pixel as [number, number];

    // 1) Try JSON
    const jsonUrl = this.buildGetFeatureInfoUrl({ base, params, map, pixel, infoFormat: "application/json" });
    try {
      const res = await fetch(jsonUrl);
      const text = await res.text();

      if (res.ok && !this.looksLikeServiceException(text)) {
        try {
          const data = JSON.parse(text);
          const features = this.extractFeatureProperties(data);
          if (features.length > 0) return features;
        }
        catch {
          // Not valid JSON — fall through to HTML fallback
        }
      }
    }
    catch {
      // Network error — fall through to HTML fallback
    }

    // 2) Fallback to HTML
    const htmlUrl = this.buildGetFeatureInfoUrl({ base, params, map, pixel, infoFormat: "text/html" });
    try {
      const res = await fetch(htmlUrl);
      if (!res.ok) return [];
      const html = await res.text();
      return this.parseHtmlFeatureInfo(html);
    }
    catch {
      return [];
    }
  }

  private looksLikeServiceException(text: string): boolean {
    return text.includes("ServiceExceptionReport") || text.includes("ServiceException");
  }

  /** Extracts an array of property objects from a GeoJSON FeatureCollection/Feature. */
  private extractFeatureProperties(data: any): Array<Record<string, any>> {
    if (!data) return [];

    if (Array.isArray(data.features)) {
      return data.features
        .map((f: any) => f?.properties ?? {})
        .filter((p: Record<string, any>) => Object.keys(p).length > 0);
    }

    if (data.properties && Object.keys(data.properties).length > 0) {
      return [data.properties];
    }

    return [];
  }

  /**
   * Parses an HTML GetFeatureInfo response into an array of property objects.
   * Horizontal tables (header row + data rows) are flipped to a vertical
   * property/value layout; tables that are already vertical are kept as-is.
   */
  private parseHtmlFeatureInfo(html: string): Array<Record<string, any>> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const tables = Array.from(doc.querySelectorAll("table"));
    const results: Array<Record<string, any>> = [];

    for (const table of tables) {
      const rows = Array.from(table.querySelectorAll("tr"));
      if (rows.length === 0) continue;

      const headerCells = Array.from(rows[0].querySelectorAll("th"));

      if (headerCells.length > 0) {
        // Horizontal table: header row + one or more data rows → flip to vertical.
        const headers = headerCells.map(cell => (cell.textContent ?? "").trim());

        for (let i = 1; i < rows.length; i++) {
          const cells = Array.from(rows[i].querySelectorAll("td"));
          if (cells.length === 0) continue;

          const obj: Record<string, any> = {};
          cells.forEach((cell, index) => {
            const key = headers[index] || `Kolom ${index + 1}`;
            obj[key] = (cell.textContent ?? "").trim();
          });

          if (Object.keys(obj).length > 0) results.push(obj);
        }
      }
      else {
        // Already a vertical property/value table (two cells per row).
        const obj: Record<string, any> = {};
        for (const row of rows) {
          const cells = Array.from(row.querySelectorAll("td"));
          if (cells.length >= 2) {
            const key = (cells[0].textContent ?? "").trim();
            const value = (cells[1].textContent ?? "").trim();
            if (key) obj[key] = value;
          }
        }
        if (Object.keys(obj).length > 0) results.push(obj);
      }
    }

    return results;
  }

  /** Builds the popup HTML for one or more layers returning feature info. */
  public buildFeatureInfoPopupContent(
    results: Array<{ layerDisplayName: string; features: Array<Record<string, any>> }>
  ): string {
    const totalRecords = results.reduce((total, result) => total + result.features.length, 0);
    const countLine = totalRecords > 1
      ? `<p class="popup-feature-count">${totalRecords} items gevonden</p>`
      : "";

    const bodyBlocks = results.map((result, layerIndex) => {
      const layerTitle = this.escapeFeatureHtml(result.layerDisplayName || `Kaartlaag ${layerIndex + 1}`);
      const itemCount = result.features.length;
      const itemLabel = itemCount === 1 ? "item" : "items";

      const itemBlocks = result.features.map((properties, itemIndex) => {
        const rows = Object.entries(properties)
          .filter(([, value]) => this.isFilledFeatureValue(value))
          .map(([key, value]) =>
            `<label>${this.escapeFeatureHtml(this.formatFeatureLabel(key))}</label> ${this.escapeFeatureHtml(this.formatFeatureValue(value))} <br/>`
          )
          .join("");

        const idLabel = this.getItemIdLabel(properties);
        const itemOpen = itemIndex === 0 ? "open" : "";
        return `
          <details class="popup-feature-item" ${itemOpen}>
            <summary>
              <span class="summary-content">${itemIndex + 1}${idLabel ? ` - ${this.escapeFeatureHtml(idLabel)}` : ""}</span>
              <span class="material-icons popup-accordion-arrow popup-accordion-arrow-item" aria-hidden="true">expand_more</span>
            </summary>
            <div class="popup-data">${rows || "<span>Geen eigenschappen gevonden</span>"}</div>
          </details>
        `;
      }).join("");

      const layerOpen = results.length === 1 ? "open" : "";
      return `
        <details class="popup-feature-accordion" ${layerOpen}>
          <summary>
            <span class="summary-content">${layerTitle} (${itemCount} ${itemLabel})</span>
            <span class="material-icons popup-accordion-arrow popup-accordion-arrow-main" aria-hidden="true">expand_more</span>
          </summary>
          <div class="popup-feature-accordion-body">
            ${itemBlocks || "<p>Geen items gevonden</p>"}
          </div>
        </details>
      `;
    }).join("");

    return `
      <div class="popup-content popup-layer-feature-info">
        <div class="popup-feature-header">
          <div class="popup-feature-title-group">
            <h5>Kaartlaag info</h5>
            ${countLine}
          </div>
          <a href="#" id="link-download-feature-csv" class="lnk lnk-icon tooltip-action" data-tooltip="Download kaartlaag info als CSV" aria-label="Download kaartlaag info als CSV">
            <span class="material-icons" style="font-size:18px">download</span>
          </a>
        </div>
        <div class="popup-feature-body">
          ${bodyBlocks}
        </div>
      </div>
    `;
  }

  private isFilledFeatureValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value as Record<string, unknown>).length > 0;
    return true;
  }

  private formatFeatureLabel(key: string): string {
    const spaced = key
      .replace(/[_-]+/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .trim();
    if (!spaced) return key;
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  private formatFeatureValue(value: unknown): string {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (Array.isArray(value)) return value.join(", ");
    return JSON.stringify(value);
  }

  /**
   * Picks a readable item identifier from keys like id/ID/Id or keys containing id.
   * If multiple candidates exist, GUID values take precedence.
   */
  private getItemIdLabel(properties: Record<string, any>): string | null {
    const entries = Object.entries(properties)
      .filter(([, value]) => this.isFilledFeatureValue(value));

    if (entries.length === 0) return null;

    const idCandidates = entries.filter(([key, value]) => {
      if (!key.toLowerCase().includes("id")) return false;
      const formatted = this.formatFeatureValue(value);
      return !this.isIgnoredIdValue(formatted);
    });
    if (idCandidates.length === 0) return null;

    const guidCandidate = idCandidates.find(([, value]) => this.isGuidValue(this.formatFeatureValue(value)));
    if (guidCandidate) return this.formatFeatureValue(guidCandidate[1]);

    const exact = idCandidates.find(([key]) => /^id$/i.test(key.trim()));
    if (exact) return this.formatFeatureValue(exact[1]);

    return this.formatFeatureValue(idCandidates[0][1]);
  }

  private isGuidValue(value: string): boolean {
    const normalized = value.trim().replace(/^\{/, "").replace(/\}$/, "");
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalized);
  }

  private isIgnoredIdValue(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    return normalized.length === 0 || normalized === "geen informatie" || normalized === "onbekend" || normalized === "unknown";
  }

  private escapeFeatureHtml(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /** Downloads feature info results as a CSV file. */
  private downloadFeatureInfoCsv(
    results: Array<{ layerDisplayName: string; features: Array<Record<string, any>> }>
  ): void {
    const allRows: Record<string, any>[] = [];
    for (const result of results) {
      for (const feature of result.features) {
        allRows.push({ Kaartlaag: result.layerDisplayName, ...feature });
      }
    }
    if (allRows.length === 0) return;

    const headers = [...new Set(allRows.flatMap(r => Object.keys(r)))];
    const csvRows = [headers.join(";")];
    for (const row of allRows) {
      csvRows.push(
        headers.map(h => this.escapeCsvValue(this.formatFeatureValue(row[h] ?? ""))).join(";")
      );
    }

    const layerName = results[0]?.layerDisplayName ?? "kaartlaag";
    this.triggerCsvDownload(`${layerName}.csv`, csvRows.join("\r\n"));
  }

  /** Downloads a single key-value object as a CSV file. */
  private downloadCsvFromObject(filename: string, data: Record<string, string>): void {
    const rows = ["Eigenschap;Waarde"];
    for (const [key, value] of Object.entries(data)) {
      rows.push(`${this.escapeCsvValue(key)};${this.escapeCsvValue(value)}`);
    }
    this.triggerCsvDownload(`${filename}.csv`, rows.join("\r\n"));
  }

  private escapeCsvValue(value: string): string {
    if (value.includes(";") || value.includes('"') || value.includes("\n")) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  private triggerCsvDownload(filename: string, csvContent: string): void {
    // BOM for Excel UTF-8 recognition
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Layer Initialization & Polygon Management ─────────────────────────────

  public initializeLayerAndEvents(
    map: atlas.Map,
    dataSourceMapRef: Map<string, atlas.source.DataSource>,
    popupRef: React.MutableRefObject<atlas.Popup | null>,
    portfolioId: string | undefined,
    portfolioActiveParticipant: boolean | undefined,
    onSelectPlanRef: React.MutableRefObject<((planId: string | null) => void) | undefined>
  ): void {
    const disciplines = plannenService.disciplines;
    const dataSourcePolygons = new atlas.source.DataSource();
    dataSourceMapRef.set('Default', dataSourcePolygons);
    map.sources.add(dataSourcePolygons);

    if (disciplines) {
      disciplines.forEach(discipline => {
        const polygonLayerDiscipline = new atlas.layer.PolygonLayer(dataSourcePolygons, undefined, {
          fillColor: discipline.colorRgb,
          fillOpacity: 0.2,
          filter: ['==', ['get', 'Discipline'], discipline.name]
        });

        const lineLayerDiscipline = new atlas.layer.LineLayer(dataSourcePolygons, undefined, {
          strokeColor: discipline.colorRgb,
          strokeWidth: 1,
          filter: ['==', ['get', 'Discipline'], discipline.name]
        });

        map.layers.add([polygonLayerDiscipline, lineLayerDiscipline], 'labels');

        // Initialize the popup
        if (!popupRef.current) {
          popupRef.current = new atlas.Popup({
            pixelOffset: [0, -20],
            closeButton: true,
          });
        }

      });

      // Add fallback layer for unknown disciplines
      const knownDisciplines: string[] = (disciplines ?? []).map(d => String(d.name).trim());
      const fallbackFilter = this.buildFallbackFilterForService('Discipline', knownDisciplines);

      const fallbackLayerPolygon = new atlas.layer.PolygonLayer(dataSourcePolygons, undefined, {
        fillColor: 'rgb(90, 90, 90)',
        fillOpacity: 0.2,
        filter: fallbackFilter
      });

      const fallbackLayerLine = new atlas.layer.LineLayer(dataSourcePolygons, undefined, {
        strokeColor: 'rgb(90, 90, 90)',
        strokeWidth: 1,
        filter: fallbackFilter
      });

      map.layers.add([fallbackLayerPolygon, fallbackLayerLine], 'labels');
    } else {
      // console.log('Geen disciplines: ', disciplines);

      const polygonLayer = new atlas.layer.PolygonLayer(dataSourcePolygons, undefined, {
        fillColor: 'rgba(0, 0, 0, 0.5)',
        strokeColor: 'rgba(0, 0, 0, 0.9)',
        strokeWidth: 2,
      });

      const lineLayer = new atlas.layer.LineLayer(dataSourcePolygons, undefined, {
        strokeColor: 'rgb(0, 0, 0)',
        strokeWidth: 1
      });

      map.layers.add([polygonLayer, lineLayer], 'labels');
    }
  }

  public createPolygonsOnMap(geoJsonData: FeatureCollection, dataSourceMapRef: Map<string, atlas.source.DataSource>): void {
    const map = mapService.getMapInstance();
    if (!map || !geoJsonData || !geoJsonData.features) return;

    // Guard: ensure map is still the active instance
    if (map !== mapService.getMapInstance()) return;

    const dataSourcePolygons = dataSourceMapRef.get("Default");
    if (!dataSourcePolygons) return;

    geoJsonData.features.forEach((feature) => {
      if (!feature.geometry) return;

      let transformedCoordinates;

      switch (feature.geometry.type) {
        case "Polygon":
          transformedCoordinates = mapService.convertRDToWGS84([feature.geometry.coordinates])[0];
          break;

        case "MultiPolygon":
          transformedCoordinates = mapService.convertRDToWGS84(feature.geometry.coordinates);
          break;

        case "LineString":
          transformedCoordinates = mapService.convertRDToWGS84(feature.geometry.coordinates);
          break;

        default:
          console.warn("Unsupported geometry type:", feature.geometry.type);
          return;
      }

      const item = new InfraPlan();
      item.infplId = feature.id?.toString();
      item.planId = feature.properties?.planId;
      item.planNaam = feature.properties?.planNaam;
      item.status = feature.properties?.planStatus;
      item.infplPublisher = feature.properties?.infplPublisher;
      item.infplPublisherName = feature.properties?.infplPublisherName;
      item.omschrijving = feature.properties?.omschrijving;
      item.discipline = feature.properties?.discipline;
      item.infplOrganizationId = feature.properties?.infplOrganizationId;
      item.infplOrganizationName = feature.properties?.infplOrganizationName;
      item.startDatum = feature.properties?.planStartDatum;
      item.eindDatum = feature.properties?.planEindDatum;

      item.bboxRD = feature.bbox?.toString() ?? "";
      item.bboxLatlng = mapService.convertBboxRDToLatLng(feature.bbox as number[], true).toString();

      const sourceDisciplineName = feature.properties?.discipline;
      const discipline = plannenService.getDisciplineByName(sourceDisciplineName);
      if (discipline) {
        item.discipline = discipline.name;
        item.colorRgb = discipline.colorRgb;
        item.colorRgba = discipline.colorRgba;
      } else {
        item.colorRgb = "rgb(236, 0, 83)";
        item.colorRgba = "rgba(236, 0, 83, 0.1)";
      }

      let shape;

      switch (feature.geometry.type) {
        case "Polygon":
          shape = new atlas.data.Polygon(transformedCoordinates);
          break;

        case "MultiPolygon":
          shape = new atlas.data.MultiPolygon(transformedCoordinates);
          break;

        case "LineString":
          shape = new atlas.data.LineString(transformedCoordinates);
          break;
      }

      if (shape && dataSourcePolygons) {
        const shapePolygonFeature = new atlas.Shape(
          new atlas.data.Feature(shape, {
            item,
            infplId: item.infplId,
            Discipline: discipline?.name || item.discipline,
            rgbaColor: item.colorRgba,
          })
        );

        dataSourcePolygons.add(shapePolygonFeature);
      }
    });
  }

  private buildFallbackFilterForService(field: string, known: string[]): any {
    const equalsAny = ['any', ...known.map(v => ['==', ['get', field], v])];
    return [
      'any',
      ['!', ['has', field]],
      ['!', equalsAny]
    ];
  }

  private isValidPosition(position: atlas.data.Position | undefined): boolean {
    if (!position || !Array.isArray(position) || position.length < 2) return false;
    const [lng, lat] = position;
    return typeof lng === 'number' && typeof lat === 'number' && 
           isFinite(lng) && isFinite(lat) &&
           lng >= -180 && lng <= 180 &&
           lat >= -90 && lat <= 90;
  }

  // ─── Click Handling ────────────────────────────────────────────────────────

  public handleMapClick(
    e: atlas.MapMouseEvent,
    map: atlas.Map,
    popupRef: React.MutableRefObject<atlas.Popup | null>,
    dataSourceMap: Map<string, atlas.source.DataSource>,
    onSelectPlanRef: React.MutableRefObject<((planId: string | null) => void) | undefined>,
    portfolioId: string | undefined,
    portfolioActiveParticipant: boolean | undefined
  ): boolean {
    try {
      // console.log('🔍 handleMapClick started, e.pixel:', e.pixel, 'e.position:', (e as any).position);
      
      // Use position from event (most reliable)
      const position: atlas.data.Position | undefined = (e as any).position;
      
      if (!position || !this.isValidPosition(position)) {
        console.warn('⚠️ Position invalid:', position);
        return false;
      }

      // console.log('✅ Valid position (clicked):', position);

      // Manually collect shapes from all DataSources and check if they contain the click position
      const seenIds = new Set<string>();
      const planItems: InfraPlan[] = [];

      // Loop through all DataSources and collect shapes that contain the clicked position
      dataSourceMap.forEach((dataSource, key) => {
        // console.log(`📍 Checking DataSource: ${key}`);
        try {
          const shapes = dataSource.getShapes();
          // console.log(`  Found ${shapes.length} total shapes in ${key}`);
          
          let shapeCount = 0;
          let noInfplIdCount = 0;
          let withinBoundsCount = 0;
          
          for (const shape of shapes) {
            const props = shape instanceof atlas.Shape
              ? shape.getProperties()
              : (shape as atlas.data.Feature<atlas.data.Geometry, any>).properties;

            shapeCount++;
            
            // Log shapes without infplId so we can see what's happening with water
            if (!props?.infplId) {
              noInfplIdCount++;
              if (noInfplIdCount <= 3) { // Log first 3 for brevity
                // console.log(`    - Shape ${shapeCount} has no infplId. Props:`, Object.keys(props || {}));
              }
              continue;
            }
            
            if (seenIds.has(props.infplId)) continue;
            
            // Get bounding box of the shape
            let geometry: any;
            if (shape instanceof atlas.Shape) {
              geometry = shape.getCoordinates ? shape.getCoordinates() : null;
            } 
            else {
              geometry = (shape as atlas.data.Feature<atlas.data.Geometry, any>).geometry?.coordinates;
            }

            // Check if clicked position is inside this polygon
            if (geometry && this.pointInPolygon(position as [number, number], geometry)) {
              withinBoundsCount++;
              // console.log(`    ✅ Position inside shape ${props.infplId} (${props.planNaam})`);
              seenIds.add(props.infplId);
              planItems.push(props.item as InfraPlan);
            }
          }
          
          // console.log(`  Summary: ${shapeCount} total, ${noInfplIdCount} without infplId, ${withinBoundsCount} within bounds`);
        } 
        catch (err) {
          console.warn(`⚠️ Error getting shapes from ${key}:`, err);
        }
      });

      // console.log('✅ Found', planItems.length, 'plan items AT clicked position');

      if (planItems.length === 0) {
        // console.log('No plan items at clicked position, closing popup');
        if (popupRef.current) popupRef.current.close();
        if (onSelectPlanRef.current) onSelectPlanRef.current(null);
        else eventBus.emit('selectPlan', null);
        return false;
      }

      if (planItems.length === 1) {
        const item = planItems[0];
        if (item.infplId) {
          if (onSelectPlanRef.current) onSelectPlanRef.current(item.infplId);
          else eventBus.emit('selectPlan', item.infplId);
        }
        // console.log('Single item at position, showing popup with position:', position);
        this.showPlanPopup(item, position, map, popupRef, portfolioId, portfolioActiveParticipant);
        return true;
      }

      // Multiple items - use picker
      // console.log('Multiple items at position, showing picker with position:', position);
      this.showPlanPicker(planItems, position, map, popupRef, onSelectPlanRef, portfolioId, portfolioActiveParticipant);
      return true;
    }
    catch (error) {
      console.error('❌ ERROR in handleMapClick:', error, { 
        pixel: e?.pixel,
        errorMessage: (error as Error)?.message,
        errorStack: (error as Error)?.stack
      });
      return false;
    }
  }

  // Helper method to check if a point is inside a polygon (ray casting algorithm)
  private pointInPolygon(point: [number, number], polygonCoordinates: any): boolean {
    if (!Array.isArray(polygonCoordinates) || polygonCoordinates.length === 0) return false;

    // Detect geometry nesting level using the 3rd element:
    //   LineString  → coords[0]       is a position  → coords[0][0] is a number
    //   Polygon     → coords[0]       is a ring      → coords[0][0] is a position → coords[0][0][0] is a number
    //   MultiPolygon→ coords[0]       is a polygon   → coords[0][0] is a ring    → coords[0][0][0] is a position → number

    if (!Array.isArray(polygonCoordinates[0])) return false;

    if (!Array.isArray(polygonCoordinates[0][0])) {
      // LineString: direct array of positions
      return this.raycastRing(point, polygonCoordinates);
    }

    if (!Array.isArray(polygonCoordinates[0][0][0])) {
      // Polygon: array of rings — check exterior ring only
      return this.raycastRing(point, polygonCoordinates[0]);
    }

    // MultiPolygon: array of polygons — check exterior ring of each sub-polygon
    for (const polygon of polygonCoordinates) {
      if (Array.isArray(polygon) && Array.isArray(polygon[0]) && this.raycastRing(point, polygon[0])) {
        return true;
      }
    }
    return false;
  }

  private raycastRing(point: [number, number], ring: any[]): boolean {
    const [lng, lat] = point;
    if (!ring || ring.length < 3) return false;
    let inside = false;

    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1];
      const xj = ring[j][0], yj = ring[j][1];

      if (typeof xi !== 'number' || typeof yi !== 'number') return false;

      const intersect = ((yi > lat) !== (yj > lat))
        && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  public showPlanPopup(
    item: InfraPlan,
    position: atlas.data.Position,
    map: atlas.Map,
    popupRef: React.MutableRefObject<atlas.Popup | null>,
    portfolioId: string | undefined,
    portfolioActiveParticipant: boolean | undefined
  ): void {
    if (!popupRef.current) return;

    // Extra strict validation
    // console.log('🎯 showPlanPopup - position:', position, 'type:', typeof position, 'isArray:', Array.isArray(position));
    
    if (!Array.isArray(position) || position.length !== 2) {
      console.error('❌ Position is not a valid array [lng, lat]:', position);
      return;
    }

    const [lng, lat] = position;
    // console.log('🎯 Position decomposed - lng:', lng, 'lat:', lat);
    
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      console.error('❌ Position contains non-finite values:', { lng, lat });
      return;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.error('❌ Position out of bounds:', { lng, lat });
      return;
    }

    const content = this.buildPlanPopupContent(item, portfolioId, portfolioActiveParticipant);
    
    try {
      // console.log('🎯 Setting popup options with valid position:', { lng, lat });
      popupRef.current.setOptions({ content, position });
      // console.log('🎯 Popup options set, opening...');
      popupRef.current.open(map);
      // console.log('✅ Popup opened successfully');
    }
    catch (error) {
      console.error('❌ Error opening plan popup:', error, { position, item: item.infplId });
      return;
    }

    setTimeout(() => {
      if (portfolioId && portfolioActiveParticipant) {
        const addPlanLink = document.getElementById('link-add-plan');
        if (addPlanLink) {
          addPlanLink.addEventListener('click', (event) => {
            event.preventDefault();
            const planId = addPlanLink.getAttribute('data-id');
            if (planId) {
              popupRef.current?.close();
              eventBus.emit('openAddPlanDialog', planId, portfolioId);
            }
          });
        }
      }

      const openPlanLink = document.getElementById('link-open-plan');
      if (openPlanLink) {
        openPlanLink.addEventListener('click', (event) => {
          event.preventDefault();
          const planId = openPlanLink.getAttribute('data-id');
          if (planId) {
            popupRef.current?.close();
            if (portfolioId) {
              window.open(`/plannen/${planId}`, '_blank', 'noopener,noreferrer');
            }
            else {
              window.location.assign(`/plannen/${planId}`);
            }
          }
        });
      }

      const csvPlanBtn = document.getElementById('link-download-plan-csv');
      if (csvPlanBtn) {
        csvPlanBtn.addEventListener('click', (event) => {
          event.preventDefault();
          const data: Record<string, string> = {
            'PlanID': item.planId || '',
            'Naam': item.planNaam || '',
            'Organisatie': item.infplOrganizationName || '',
            'Status': item.status || '',
            'Discipline': item.discipline || '',
            'Startdatum': item.startDatum ? new Date(item.startDatum).toLocaleDateString('nl-NL') : '',
            'Einddatum': item.eindDatum ? new Date(item.eindDatum).toLocaleDateString('nl-NL') : '',
          };
          this.downloadCsvFromObject('plan', data);
        });
      }
    }, 100);
  }

  public buildPlanPopupContent(
    item: InfraPlan,
    portfolioId: string | undefined,
    portfolioActiveParticipant: boolean | undefined
  ): string {
    let addPlanToPortfolioLink = '';
    if (portfolioId && portfolioActiveParticipant) {
      addPlanToPortfolioLink = `
        <a href="#" id="link-add-plan" data-id="${item.infplId}" 
          class="lnk lnk-icon-button tooltip-action" 
          data-tooltip="Plan uitnodigen voor dit dossier"
          aria-label="Plan uitnodigen voor dit dossier"
        >
          <i class="material-icons">add</i>
          <span>Plan</span>
        </a>
      `;
    }

    const openPlanTarget = portfolioId ? ' target="_blank" rel="noopener noreferrer"' : '';
    const openPlanTitle = portfolioId ? 'Open het plan in een nieuw venster' : 'Open het plan';
    const openPlanIcon = portfolioId ? 'open_in_new' : 'arrow_forward';

    return `
      <div class="popup-content popup-plan">
        <h5>${item.planNaam || 'Geen'}</h5>
        <span 
          class="category-icon category-icon-small ${helperService.toCamelCase(item.discipline)}-icon-white tooltip-action" 
          data-tooltip="${item.discipline}"
          aria-label="${item.discipline}">
        </span>
        <div class="popup-data">
          <label>PlanID</label> ${item.planId || 'Geen'} <br/>
          <label>Organisatie</label> ${item.infplOrganizationName || 'Geen'} <br/>
          <label>Status</label> ${item.status || 'Geen'} <br/>
          <label>Startdatum</label> ${item.startDatum ? new Date(item.startDatum).toLocaleDateString('nl-NL') : '-'} <br/>
          <label>Einddatum</label> ${item.eindDatum ? new Date(item.eindDatum).toLocaleDateString('nl-NL') : '-'} <br/>
        </div>
        <div class="popup-btn-group">
          ${addPlanToPortfolioLink}
          <a href="#" id="link-download-plan-csv" class="lnk lnk-icon tooltip-action hidden" data-tooltip="Download plan info als CSV" aria-label="Download plan info als CSV">
            <span class="material-icons">download</span>
          </a>
          <a href="/plannen/${item.infplId}"${openPlanTarget} id="link-open-plan" data-id="${item.infplId}" class="lnk lnk-icon tooltip-action" data-tooltip="${openPlanTitle}" aria-label="${openPlanTitle}">
            <span class="${portfolioId ? 'material-symbols-outlined' : 'material-icons'}">${openPlanIcon}</span>
          </a>
        </div>
      </div>
    `;
  }

  private showPlanPicker(
    items: InfraPlan[],
    position: atlas.data.Position,
    map: atlas.Map,
    popupRef: React.MutableRefObject<atlas.Popup | null>,
    onSelectPlanRef: React.MutableRefObject<((planId: string | null) => void) | undefined>,
    portfolioId: string | undefined,
    portfolioActiveParticipant: boolean | undefined
  ): void {
    if (!popupRef.current) return;

    // console.log('showPlanPicker - position:', position, 'items count:', items.length);

    const listItems = items.map(item => `
      <li>
        <a href="#" class="plan-picker-item" data-id="${item.infplId}">
          <span class="category-icon category-icon-small ${helperService.toCamelCase(item.discipline)}-icon-white"></span>
          <span class="plan-picker-name">${item.planNaam || item.planId || 'Onbekend plan'}</span>
        </a>
      </li>
    `).join('');

    const content = `
      <div class="popup-content popup-plan-picker">
        <h5>${items.length} overlappende plannen</h5>
        <ul class="plan-picker-list">${listItems}</ul>
      </div>
    `;

    try {
      popupRef.current.setOptions({ content, position });
      popupRef.current.open(map);
    }
    catch (error) {
      console.error('Error opening plan picker:', error, { position, count: items.length });
      return;
    }

    setTimeout(() => {
      items.forEach(item => {
        const links = document.querySelectorAll<HTMLElement>(`.plan-picker-item[data-id="${item.infplId}"]`);
        links.forEach(link => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            if (item.infplId) {
              if (onSelectPlanRef.current) onSelectPlanRef.current(item.infplId);
              else eventBus.emit('selectPlan', item.infplId);
            }
            this.showPlanPopup(item, position, map, popupRef, portfolioId, portfolioActiveParticipant);
          });
        });
      });
    }, 100);
  }

};

// Export an instance of the LayerService class
export const layerService = LayerService.getInstance();
